import { makeStyles, useTheme } from "@material-ui/core";
import { Delete, Sync } from "@mui/icons-material";
import DoneIcon from '@mui/icons-material/Done';
import UndoIcon from '@mui/icons-material/Undo';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  Tooltip
} from "@mui/material";
import moment from 'moment';
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import ApiConfig from 'src/Apiconfig/config';
import {
  rawAPICall
} from "src/Apiconfig/service";
import { AuthContext } from "src/context/Auth";
import { useWalletUpdate } from 'src/hooks/useWalletUpdate';
import "../../scss/main.css";
import AlertDialog from './utils/AlertDialog.tsx';
import { Dialog } from '@mui/material';
import FileUploadDialog from 'src/views/pages/FileUploadDialog'
import { getAPIHandler } from 'src/Apiconfig/service';
import { display } from "@mui/system";
import BoxSkeleton from 'src/component/loaders/BoxSkeleton';
import DataNotFound from 'src/component/DataNotFound';
import { useSnackbar } from "src/context/SnackbarProvider";
import ManageChainsDialog from './ManageChainsDialog';
import WhichWalletsAreYoursModal from 'src/views/assest/WhichWalletsAreYoursModal';

const useStyles = makeStyles((theme) => ({
  walletContainer: {
    //backgroundColor: theme.name === "light" ? "#F7F7F8" : "#0F0E14",
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  contentContainer: {
    //backgroundColor: theme.name === "light" ? "#FFF" : "#0F0E14",
    overflow: "hidden",
    minHeight: 300,
    width: "90%",
    margin: "auto",
    padding: "20px",
  },
  walletCard: {
    height: "180px",
    backgroundColor: theme.name === "LIGHT" ? "#FFF !important" : "#1C1825 !important",
    color: theme.name === "LIGHT" ? "#000 !important" : "#FFF !important",
    borderRadius: "14px !important",
    padding: "16px !important",
    border: theme.name === "LIGHT" ? "1px solid rgba(0, 0, 0, 0.06)" : "1px solid rgba(255, 255, 255, 0.06)",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1) !important",
    fontFamily: '"Manrope", Helvetica, sans-serif !important',
    position: "relative",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: theme.name === "LIGHT"
        ? "0px 8px 20px rgba(0, 0, 0, 0.12) !important"
        : "0px 8px 20px rgba(0, 0, 0, 0.4) !important",
      transform: "translateY(-2px)",
    },
    // Cards with a 3rd action icon (MetaMask's Manage Chains, alongside delete/sync) can
    // exceed the card's width at narrower breakpoints since walletHeader below has no
    // flex-shrink control - clip defensively so nothing ever visually breaks out of the
    // card boundary regardless of icon count.
    overflow: "hidden",
  },
  textClr: {
    color: theme.name === "LIGHT" ? "#000 !important" : "#FFF !important",
  },
  walletHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  walletIcon: {
    width: "40px",
    height: "40px",
    backgroundColor: "#5A5A5A",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "bold",
  },
  walletDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  walletActions: {
    //color: theme.palette.text.secondary,
    color: 'white',
  },
  sectionTitle: {
    //color: theme.palette.text.secondary,
    fontSize: "14px",
  },
  sectionValue: {
    // fontWeight: "bold",
    color: "inherit",
    //fontSize: "15px",
  },
  footerText: {
    color: theme.palette.text.secondary,
    textAlign: "right",
    marginTop: "16px",
  },
  walletActionsContainer: {
    display: "flex",
    gap: "2px",  // Control the spacing between the two icon spaces
    // Never shrink the actions column - the name/address block to its left yields first
    // (via minWidth:0 + its existing text-ellipsis), so all 2-3 icons always stay fully
    // visible instead of one getting squeezed or clipped when a card has 3 icons.
    flexShrink: 0,
  },
  walletAction: {
    display: "flex",
    alignItems: "center",
    "& .MuiIconButton-root": {
      padding: "4px",
      backgroundColor: "transparent",
      margin: 0,
      "&:hover": {
        backgroundColor: "transparent",
      },
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    // "& img": {
    //   width: "18px",
    //   height: "18px",
    //   display:"block",
    // },
  },
}));


// "45s", "2m 14s", "1h 05m" - short enough to sit inline next to a progress percentage without
// wrapping the footer onto a second line on a narrow card.
const formatDurationShort = (ms) => {
  if (ms === null || ms === undefined || isNaN(ms) || ms < 0) return null;
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  return `${seconds}s`;
};

// navigator.clipboard.writeText() returns a Promise that can reject (page not focused,
// permissions-policy blocking it, non-secure context) - the two copy buttons below used to fire
// it and then unconditionally show "Copied!" regardless of whether the write actually succeeded,
// so a silent rejection looked identical to success while nothing landed on the clipboard. This
// awaits the real result and falls back to the older execCommand approach (works in more
// restricted contexts) before reporting failure.
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    throw new Error("Clipboard API unavailable");
  } catch (err) {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const succeeded = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (!succeeded) throw new Error("execCommand copy failed");
      return true;
    } catch (fallbackErr) {
      console.error("Copy to clipboard failed:", fallbackErr);
      return false;
    }
  }
};

const Wallets = () => {
  const theme = useTheme();
  const classes = useStyles();
  const auth = useContext(AuthContext);
  const loading = auth?.isLoading || false;
  const [wallets, setWallets] = useState([]);
  const [walletMetrics, setWalletMetrics] = useState([]);
  const [userWalletStatus, setUserWalletStatus] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRefreshed, setIsRefreshed] = useState(false)
  const [showWhichWalletsModal, setShowWhichWalletsModal] = useState(false)
  const [loadingStates, setLoadingStates] = useState({});
  const { walletConfigCache, userData } = useContext(AuthContext);
  const currency = auth?.userProfileInfo?.currency;
  const symbol = auth?.userProfileInfo?.symbol;
  // assetTotal only ever carries whatever currencies the backend actually priced (USD/INR today),
  // keyed by currency code - if the viewer's own profile currency preference isn't set (or, while
  // an accountant is acting as a client, auth.userProfileInfo is still the ACCOUNTANT's own
  // locally-cached profile, not the client's), assetTotal[currency] is silently undefined even
  // though real priced data exists right there under a different key. Falls back to USD, then
  // whatever's actually present, instead of a bare "N/A" when that happens.
  const FALLBACK_CURRENCY_SYMBOLS = { USD: "$", INR: "₹" };
  const resolveAssetTotal = (assetTotal) => {
    if (!assetTotal) return { value: undefined, symbol };
    if (assetTotal[currency] !== undefined) return { value: assetTotal[currency], symbol };
    if (assetTotal.USD !== undefined) return { value: assetTotal.USD, symbol: FALLBACK_CURRENCY_SYMBOLS.USD };
    const [fallbackCurrency] = Object.keys(assetTotal);
    return fallbackCurrency
      ? { value: assetTotal[fallbackCurrency], symbol: FALLBACK_CURRENCY_SYMBOLS[fallbackCurrency] || "" }
      : { value: undefined, symbol };
  };
  const [dialogData, setDialogData] = useState({
    isOpen: false,
    walletName: "",
    action: "",
    title: "",
    description: "",
  });
  const [metricsLoaded, setMetricsLoaded] = useState(false);
  const [walletsLoaded, setWalletsLoaded] = useState(false);
  const [manageChainsWallet, setManageChainsWallet] = useState(null);
  const [isDialogActionInProgress, setIsDialogActionInProgress] = useState(false);
  const dialogActionInProgressRef = useRef(false);
  const previousStatusesRef = useRef({});
  const { showSnackbar, registerPageRefresh } = useSnackbar();

  const openDialog = (wallet, action) => {
    const walletName = wallet.name;
    const walletId = wallet.walletId;

    const matchedWalletConfig = walletConfigCache?.find(
      (cfg) => cfg.name.trim().toLowerCase() === walletName?.trim().toLowerCase()
    );

    const actionTitles = {
      archive: "Archive Wallet",
      unarchive: "UnArchive Wallet",
      delete: "Delete Wallet",
      sync: "Sync Wallet",
      fileupload: "Upload Wallet File",
    };

    const actionDescriptions = {
      archive: "Are you sure you want to Archive this Wallet?",
      unarchive: "Are you sure you want to UnArchive this Wallet?",
      delete: "Are you sure you want to Delete this wallet? This action cannot be undone.",
      sync: `Are you sure want to Sync the wallet ${walletName}?`,
      fileupload: `Upload your transaction file for ${walletName}`,
    };

    setDialogData({
      isOpen: true,
      walletName,
      walletId,
      action,
      title: actionTitles[action],
      description: actionDescriptions[action],
      wallet: {
        ...wallet, // from userAddedWallets (has walletId, aliasName, etc.)
        ...matchedWalletConfig, // from walletConfigCache (has configurations, fileTypes, etc.)
        userId: userData?.uid,
      },
    });
  };


  // Function to fetch user wallets
const fetchUserWallets = useCallback(async () => {
  try {
    const response = await rawAPICall({
      method: "GET",
      url: `${ApiConfig["wallet"]}`,
    });

    if (response.status === 200) {
      const fetchedWallets = response.data || [];
      auth?.setUserAddedWallets?.(fetchedWallets);
      setWallets(fetchedWallets);
      setWalletsLoaded(true);
      auth?.setIsLoading?.(false);
      return;
    }

    // ANY non-200 response → treat as no data
    setWallets([]);
    auth?.setUserAddedWallets?.([]);
    setWalletsLoaded(true);
    auth?.setIsLoading?.(false);

  } catch (error) {
    console.error("Error fetching user wallets:", error);

    // ANY error → treat as no data
    setWallets([]);
    auth?.setUserAddedWallets?.([]);

    setWalletsLoaded(true);
    auth?.setIsLoading?.(false);
  }
}, [auth?.userData?.uid]);

  // Function to fetch wallet metrics
  const fetchWalletMetrics = useCallback(async () => {
    try {
      const response = await rawAPICall({
        method: "GET",
        url: `${ApiConfig["walletMetrics"]}`,
      });

      if (response.status === 200) {
        // Ensure we always set an array, even if the response is empty or undefined
        const metrics = response.data?.walletMetrics || [];
        setWalletMetrics(metrics);
        setMetricsLoaded(true);
        console.log("Fetched wallet metrics:", metrics);
      }
      console.log("Wallet Metrics Response:", response.data.walletMetrics);
    } catch (error) {
      console.error("Error fetching wallet metrics:", error);
      toast.error("Failed to load wallet metrics.");
      // Set empty array on error to prevent undefined issues
      setWalletMetrics([]);
      setMetricsLoaded(true);
    }
  }, [auth?.userData?.uid, currency]);
  //fetch the user wallet sync status
  const fetchUserWalletStatus = useCallback(async () => {
    try {
      const response = await rawAPICall({
        method: "GET",
        url: `${ApiConfig["load"]}/status`,
      });

      if (response.status === 200) {
        const walletStatuses = response.data?.walletStatuses || [];
        const storedWallet = sessionStorage.getItem('newlyAddedWallet');
        if (storedWallet) {
          try {
            const walletId = JSON.parse(storedWallet);
            const status = walletStatuses.find(w => w.walletId === walletId);
            if (status && (
            status.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_SUCCESFULLY ||
            status.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_ERROR
          )) {
            console.log("[Wallets] Newly added wallet sync completed, clearing sessionStorage");
            sessionStorage.removeItem('newlyAddedWallet'); // ✅ Clear it
          }
          } catch (e) {
            console.error("Error parsing stored wallet info:", e);
          }
        }
        let shouldRefreshMetrics = false;
        walletStatuses.forEach(wallet => {
          const walletId = wallet.walletId;
          const prevStatus = previousStatusesRef.current[walletId];
          const currStatus = wallet.status;
          if (
            (prevStatus === "IN_PROGRESS" || prevStatus === "PUBLISHED") &&
            currStatus === "COMPLETED_SUCCESSFULLY"
          ) {
            const walletInfo = auth?.userAddedWallets?.find(
              w => w.walletId === walletId
            );
            const walletName = walletInfo?.name || "Your";

            showSnackbar(`${walletName} wallet is synced!!`);
            shouldRefreshMetrics = true;
            // Ask which wallets are the user's own right as THIS sync finishes - this is
            // the one moment that works for both a brand-new wallet and a resync of an
            // existing one, and it's the earliest point the backlog of repeat counterparty
            // addresses (from the transactions this sync just wrote) is actually queryable.
            setShowWhichWalletsModal(true);

          }
          if (
            (prevStatus === "IN_PROGRESS" || prevStatus === "PUBLISHED") &&
            currStatus === "COMPLETED_WITH_ERRORS"
          ) {
            const walletInfo = auth?.userAddedWallets?.find(
              w => w.walletId === walletId
            );
            const walletName = walletInfo?.name || "Your";

            showSnackbar(`${walletName} wallet is synced!!`);
            shouldRefreshMetrics = true;
            setShowWhichWalletsModal(true);

          }

          // Also refresh metrics on every poll tick while a wallet is actively
          // syncing, not just once when it finishes - transaction count climbs
          // progressively as pages are persisted mid-sync, so without this the
          // count sits frozen at its starting value for the whole "Syncing..."
          // duration instead of ticking up alongside the status.
          if (currStatus === "IN_PROGRESS" || currStatus === "PUBLISHED") {
            shouldRefreshMetrics = true;
          }

          previousStatusesRef.current[walletId] = currStatus;
        });
        // Transaction counts (and other wallet metrics) are a separate data flow from
        // sync status - without this, the status badge updates live but "Number of
        // Transactions" on each wallet card stays stale until a manual page refresh.
        if (shouldRefreshMetrics) {
          fetchWalletMetrics();
        }
        setUserWalletStatus(response.data);
        auth.userWalletStatus = response.data;
      }
    } catch (error) {
      console.error("Error fetching wallet Status:", error);
      toast.error("Failed to load wallet Status.");
    }
  }, [auth.userAddedWallets, fetchWalletMetrics]);

  const fetchWalletConfigurationList = async () => {
    try {
      const response = await getAPIHandler({
        endPoint: 'wallet',
        id: 'configuration',
        source: 'wallets',
      });

      if (response.status === 200) {
        auth.walletConfigCache = response.data; // save to context
      }
    } catch (error) {
      console.error('Error fetching wallet configurations:', error);
      toast.error("Failed to load wallet configuration.");
    }
  };

  // Fetch wallet metrics when component mounts or wallets update
  //const [userWalletStatus, setUserWalletStatus] = useState([]); // Ensure this starts as an empty array
  const [intervalId, setIntervalId] = useState(null); // Store the interval ID
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [shouldSpin, setShouldSpin] = useState(false);
  const intervalRef = useRef(null); // Declare ref outside useEffect
  // Dedicated to the wallet-status poll below - intervalRef above is shared by two other,
  // unrelated setInterval effects further down, so reusing it for this effect's self-rescheduling
  // setTimeout would risk clobbering their stored ids (or vice versa).
  const walletStatusPollRef = useRef(null);
  const hasInitialFetchRun = useRef(false); // Track if initial fetch has run
  useEffect(() => {
    if (!fetchUserWallets || !fetchWalletMetrics || !fetchUserWalletStatus) return;

    // Define what to do when Snackbar's Refresh button is clicked
    const refreshWalletsPage = () => {
      console.log("[Snackbar Refresh] Reloading Wallets page data...");
      fetchUserWallets();
      fetchWalletMetrics();
      fetchUserWalletStatus();
    };

    // Register this refresh handler with the Snackbar context
    registerPageRefresh(refreshWalletsPage);

  }, [registerPageRefresh, fetchUserWallets, fetchWalletMetrics, fetchUserWalletStatus]);

  // Initial fetch - runs only once when component mounts
  useEffect(() => {
    if (auth?.userData?.uid && !hasInitialFetchRun.current) {
      hasInitialFetchRun.current = true;
      fetchUserWallets(); // Fetch user wallets first
      fetchWalletMetrics();
      fetchUserWalletStatus(); // Initial fetch
    }
  }, [auth?.userData?.uid]);

  useEffect(() => {
    if (auth?.userData?.uid && currency && hasInitialFetchRun.current) {
      console.log("[Wallets] Currency changed, refetching metrics with:", currency);
      setMetricsLoaded(false); // Show loading state
      fetchWalletMetrics();
    }
  }, [currency]);

  // Polling logic - separate from initial fetch
  //
  // This used to stop polling once every wallet in `auth.userAddedWallets` looked
  // completed - but that list is captured once when this effect starts (its
  // dependency array is just the user id) and never updates for the rest of the
  // session, so any wallet added afterward was invisible to the check. The very
  // first tick would then see "all of my (stale) wallets are done" and stop
  // fetching entirely, freezing the display for anything synced later. Simplest
  // fix: keep polling on a fixed interval for as long as the user is on the page,
  // instead of trying to guess when it's safe to stop.
  useEffect(() => {
    if (auth?.userData?.uid) {
      // A one-time DEX sync typically finishes in 60-90s total, so a flat 60s poll could catch
      // it at most once mid-sync (or miss it entirely) - the percentage bar would almost never
      // actually be seen updating. Poll much faster (4s) while anything is genuinely in-flight
      // (not yet a terminal status), and fall back to the original 60s cadence once nothing is
      // syncing, so an idle Wallets page isn't hammering this endpoint for no reason.
      const hasActiveSync = () =>
        userWalletStatus?.walletStatuses?.some(
          (status) =>
            status.status !== process.env.REACT_APP_WALLET_SYNC_COMPLETED_SUCCESFULLY &&
            status.status !== process.env.REACT_APP_WALLET_SYNC_COMPLETED_ERROR
        );

      // A single scheduled fetch, not a repeating interval - fetchUserWalletStatus updates
      // userWalletStatus, which re-triggers this effect (it's a dependency below), so the next
      // fetch gets scheduled fresh each time with whatever delay the latest data calls for. A
      // self-rescheduling setInterval/setTimeout here as well would double up with that and fire
      // twice as often as intended.
      const delay = hasActiveSync() ? 4000 : 60000;
      walletStatusPollRef.current = setTimeout(() => {
        console.log(" Fetching wallet status again...");
        fetchUserWalletStatus();
      }, delay);

      return () => {
        console.log(" Cleaning up interval...");
        clearTimeout(walletStatusPollRef.current);
      };
    }
  }, [auth?.userData?.uid, userWalletStatus]);

  useEffect(() => {
    if (!walletConfigCache || walletConfigCache.length === 0) {
      fetchWalletConfigurationList();
    }
  }, []);

  // 🟣 Register Wallets page refresh logic
  useEffect(() => {
    if (!fetchUserWallets || !fetchWalletMetrics || !fetchUserWalletStatus) return;

    const refreshWalletsPage = () => {
      console.log("[Snackbar Refresh] Reloading Wallets data...");
      fetchUserWallets();
      fetchWalletMetrics();
      fetchUserWalletStatus();
    };

    registerPageRefresh(refreshWalletsPage);
  }, [registerPageRefresh, fetchUserWallets, fetchWalletMetrics, fetchUserWalletStatus]);


  console.log("Stored walletMetrics:", walletMetrics);
  useEffect(() => {
    console.log("Updated Wallet Metrics:", walletMetrics);
  }, [walletMetrics]);

  const closeDialog = () => {
    // setIsDialogOpen(false);
    setDialogData({ isOpen: false });
  };
  const handleAction = async (walletName, walletId, action) => {
    // Guards against the dialog's "Yes" button firing this handler multiple times
    // for one confirm (rapid double-clicks landing before the dialog closes) - each
    // firing issued its own DELETE/archive call, producing duplicate requests for the
    // same wallet (observed as 3 DELETE calls from a single delete confirmation).
    if (dialogActionInProgressRef.current) return;
    dialogActionInProgressRef.current = true;
    setIsDialogActionInProgress(true);
    try {
      if (action === "archive") {
        await handleArchiveWallet(walletName, walletId);
      } else if (action === "unarchive") {
        await handleUnarchive(walletName, walletId);
      } else if (action === "delete") {
        await handleDelete(walletId);
      } else if (action === "sync") {
        await handleSync(walletId);
      }
    } finally {
      dialogActionInProgressRef.current = false;
      setIsDialogActionInProgress(false);
      closeDialog();
    }
  };

  useEffect(() => {
    console.log("Current Theme:", theme);

  }, [theme]);
  useEffect(() => {
    if (auth?.userAddedWallets) {
      setWallets(auth.userAddedWallets);
      // setArchiveWallets(auth.userAddedWallets.filter(wallet => wallet.archived))
      console.log("Wallets synced from AuthContext:", auth.userAddedWallets);
      //  console.log("Archieve Wallet :", archiveWallets);
    }
  }, [auth?.userAddedWallets]);

  const handleArchiveWallet = (walletName, walletId) => {
    console.log("Deleting wallet ID:", walletId);
    return archiveWallet(walletName, walletId);
  };
  const archiveWallet = async (walletName, walletId) => {
    try {
      console.log("API Archive WALLET URL :" + `${ApiConfig["wallet"]}`)
      const response = await rawAPICall({
        method: "PUT",
        url: `${ApiConfig["wallet"]}` + "/" + walletId + "/archive",
      });
      console.log("Response : " + response);
      if (response.status == 200) {
        const result = await response.data;
        auth.setUserAddedWallets(result);
        // Update local state
        setWallets(result);
        showSnackbar(`${walletName} wallet archived successfully!`);
      }

    } catch (error) {
      toast.error('Error fetching data:', error);
    }
  };
  const handleUnarchive = async (walletName, walletId) => {
    try {
      const response = await rawAPICall({
        method: "PUT",
        url: `${ApiConfig["wallet"]}` + "/" + walletId + "/unArchive",
      });

      if (response.status == 200) {
        const result = await response.data;

        // Update context + local state
        auth.setUserAddedWallets(result);
        setWallets(result);
        showSnackbar(`${walletName} wallet UnArchived successfully!`);

        // ✅ Immediately refresh metrics and status
        fetchWalletMetrics();
        fetchUserWalletStatus();

        // ✅ Optionally start polling again if needed
        if (!intervalRef.current) {
          console.log("Restarting polling after unarchive...");
          const startPolling = () => {
            intervalRef.current = setInterval(() => {
              fetchUserWalletStatus();
            }, 60000);
          };
          startPolling();
        }
      }

    } catch (error) {
      toast.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    const handleImmediateRefresh = () => {
      console.log("[Wallets.js] Immediate refresh triggered by TabView/FileUploadDialog");
      fetchUserWallets();
      fetchWalletMetrics();
      fetchUserWalletStatus();
    };

    window.addEventListener("refreshWalletsData", handleImmediateRefresh);

    return () => {
      window.removeEventListener("refreshWalletsData", handleImmediateRefresh);
    };
  }, [fetchUserWallets, fetchWalletMetrics, fetchUserWalletStatus]);

  // In Wallets.js, add this useEffect (should already exist)
useEffect(() => {
  const handleWalletAdded = () => {
    console.log("[Wallets] walletAdded event received, refreshing wallet list");
    fetchUserWallets();
  };

  window.addEventListener("walletAdded", handleWalletAdded);

  return () => {
    window.removeEventListener("walletAdded", handleWalletAdded);
  };
}, [fetchUserWallets]);

  const handleDelete = async (walletId) => {
    try {
      console.log("API Delete URL :" + `${ApiConfig["wallet"]}`);

      setIsDialogOpen(false);

      const response = await rawAPICall({
        method: "DELETE",
        url: `${ApiConfig["wallet"]}/${walletId}`,
      });

      if (response.status === 200) {
        const updatedWallets = wallets.filter(wallet => wallet.walletId !== walletId);
        auth.setUserAddedWallets(updatedWallets);
        setWallets(updatedWallets);

        // ✅ Show snackbar with Refresh option
        showSnackbar(
          `Wallet deleted successfully!`,
          () => {
            // ✅ This callback is triggered if user clicks “Refresh” on the snackbar
            console.log("[Snackbar Refresh] Reloading data after deletion...");
            fetchUserWallets();
            fetchWalletMetrics();
            fetchUserWalletStatus();
          }
        );
      }

    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.warn("Wallet not found on the server (404). Updating local state.");
        const updatedWallets = wallets.filter(wallet => wallet.walletId !== walletId);
        auth.userAddedWallets = updatedWallets;
        setWallets(updatedWallets);

        // ✅ Notify user anyway
        showSnackbar(
          `Wallet already removed or not found.`,
          () => {
            fetchUserWallets();
            fetchWalletMetrics();
            fetchUserWalletStatus();
          }
        );

      } else {
        console.error("Error deleting wallet:", error);
        toast.error("An error occurred while deleting the wallet. Please try again.");
      }
    }
  };


  const { loadIndivitualWalletDetails } = useWalletUpdate(auth?.userData?.uid, isRefreshed)

  const handleSync = async (walletId) => {
    console.log("Button clicked in Parent, calling child function...");
    setIsRefreshed((prev) => !prev);
    setLoadingStates(prev => ({ ...prev, [walletId]: true }));
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    try {
      await loadIndivitualWalletDetails(walletId);
      console.log("Wallet details loaded. Starting polling...");

      const startPolling = () => {
        intervalRef.current = setInterval(() => {
          console.log("⏳ Checking wallet statuses...");

          setUserWalletStatus((prevStatus) => {
            console.log("PrevStatus", prevStatus);

            // Only check the status of the wallet this sync was triggered for.
            // (Previously this checked whether ALL of the user's wallets were
            // completed, using a wallet list captured when polling started —
            // if a newly added wallet wasn't in that snapshot yet, this would
            // report "done" as soon as the user's OTHER, already-finished
            // wallets matched, even though the wallet actually being synced
            // was still IN_PROGRESS.)
            const thisWalletCompleted = prevStatus?.walletStatuses?.some(
              wallet =>
                wallet.walletId === walletId &&
                (wallet.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_SUCCESFULLY ||
                  wallet.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_ERROR)
            ) ?? false;

            if (thisWalletCompleted) {
              console.log("✅ This wallet is COMPLETED! Stopping polling.");
              clearInterval(intervalRef.current);
              setIsPollingActive(false);
              setLoadingStates(prev => ({ ...prev, [walletId]: false }));
              return prevStatus;
            } else {
              console.log("🚀 Fetching wallet status again...");
              fetchUserWalletStatus(); // ✅ This is the only place it is called now
              return prevStatus;
            }
          });
        }, 60000);
      };

      startPolling();

      return () => {
        console.log("🛑 Cleaning up interval...");
        clearInterval(intervalRef.current);
      };

    } catch (error) {
      console.error("Error loading wallet details:", error);
    }
  };

  // Calculate if there are any active wallets and any archived wallets
  const activeWallets = wallets.filter(wallet => !wallet.archived);
  const archivedWallets = wallets.filter(wallet => wallet.archived);
  const hasNoWalletsAtAll = wallets.length === 0;

  return (
    <Box>
      <Box className={classes.contentContainer}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <Typography className={classes.textClr} variant="h5" sx={{
            fontFamily: '"Manrope", "Helvetica", "Arial", sans-serif',
            fontWeight: 600,
            textTransform: "none",
          }}>
            Wallets
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Button
            variant="outlined"
            component={Link}
            to="/secure/my-wallet-addresses"
            sx={{
              borderRadius: "12px",
              padding: "8px 16px",
              fontFamily: '"Manrope", Helvetica',
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            My Wallet Addresses
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowWhichWalletsModal(true)}
            sx={{
              borderRadius: "12px",
              padding: "8px 16px",
              fontFamily: '"Manrope", Helvetica',
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Find My Other Wallets
          </Button>
          <Button
            data-tour="add-wallet"
            variant="contained"
            component={Link}
            to={{
              pathname: "/secure/add-assets",
              state: { fromWalletPage: true }
            }}
            sx={{
              background: "linear-gradient(180deg, rgb(95,81,222) 0%, rgb(173,93,211) 100%)",
              color: "#FFF",
              borderRadius: "12px",
              padding: "8px 16px",
              fontFamily: '"Manrope", Helvetica',
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Add Wallet / Exchanges
          </Button>
          </Box>
        </Box>

        {/* Content */}
        {!walletsLoaded ? (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
            <CircularProgress />
          </Box>
        ) : activeWallets.length > 0 ? (
          <Grid container spacing={3}>
            {activeWallets.map((wallet, index) => {
              const effectiveWalletId = wallet.parentWalletId || wallet.walletId;

              // Own metrics, not the parent's - a merged wallet's own asset-holdings/transactions
              // stay recorded under its own walletId. The parent (post-merge) has a genuinely
              // different set of holdings for the same chain name, so falling back to
              // parentWalletId here would show a different wallet's data under this card.
              const walletMetric = Array.isArray(walletMetrics)
                ? walletMetrics.find(
                  metric =>
                    metric.walletId === wallet.walletId &&
                    metric.source?.toLowerCase() === wallet.name.toLowerCase()
                )
                : undefined;

              console.log("🔍 Matching wallet:", {
                walletName: wallet.name,
                effectiveWalletId,
                foundMetric: walletMetric,
                allMetrics: walletMetrics
              });

              const resolvedTotal = resolveAssetTotal(walletMetric?.assetTotal);
              const totalValueFormatted = resolvedTotal.value !== undefined
                ? resolvedTotal.symbol + new Intl.NumberFormat().format(resolvedTotal.value.toFixed(2))
                : "N/A";

              const transactionCount = walletMetric?.transactionCount ?? "N/A";

              // Check if this wallet's ID matches the one in userWalletStatus. A file-upload
              // wallet's sync can complete in well under the time it takes the next status poll
              // to land (the backend creates its request-status record within a couple seconds
              // of upload, but this card's data only reflects it once userWalletStatus has been
              // re-fetched) - without this, a wallet mid-sync can show no status entry at all
              // yet and fall through to a bare "N/A" instead of any syncing indicator. The same
              // uploadInProgress_<name> flag the file-upload icon's tooltip already trusts for
              // this exact signal covers that gap.
              const currentWalletStatus = userWalletStatus?.walletStatuses?.find(
                status => status.walletId === effectiveWalletId
              );
              const fileUploadInProgress = wallet.fileUploadEnabled
                && localStorage.getItem(`uploadInProgress_${wallet.name}`) === 'true';
              // A sync's background worker can get cut off mid-flight (its Cloud Run instance
              // recycled by a deploy or scale-down event) with no error ever reported - the
              // tracking-service timeout sweep is what eventually resolves that stuck record to
              // COMPLETED_WITH_TIMEOUT, so it has to be treated as a terminal, non-loading state
              // here exactly like COMPLETED_WITH_ERRORS, or this card would keep showing a dead
              // sync as "Syncing X%" forever even after the backend gave up on it.
              const hasTimedOut = currentWalletStatus?.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_TIMEOUT;
              const hasError = currentWalletStatus?.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_ERROR || hasTimedOut;
              const isLoading = fileUploadInProgress || (
                currentWalletStatus
                && currentWalletStatus.status !== process.env.REACT_APP_WALLET_SYNC_COMPLETED_SUCCESFULLY
                && !hasError
              );
              const hasDeleteIcon = currentWalletStatus
                && (currentWalletStatus.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_SUCCESFULLY || hasError);
              // 0 means either "hasn't reported real progress yet" or "this sync type doesn't
              // report it at all" (most exchanges, for now - see SyncProgressTracker.NOOP on the
              // backend) - both cases fall back to the existing indeterminate spinner rather than
              // a determinate bar frozen at 0%.
              const syncProgressPercent = currentWalletStatus?.progressPercent || 0;
              // Real wall-clock timing, not a guess - createdAt is when tracking-service first
              // recorded this sync, lastUpdatedAt is its most recent progress report. elapsedMs
              // drives the on-screen timer; etaMs is a simple linear projection from how long it
              // took to reach the current percent, which is exactly as accurate as that
              // assumption (early-stage estimates swing around until there's enough progress to
              // trust the trend - hidden below 5% for that reason).
              const syncStartedAt = currentWalletStatus?.createdAt ? moment(currentWalletStatus.createdAt) : null;
              const elapsedMs = syncStartedAt ? Math.max(0, moment().diff(syncStartedAt)) : null;
              const etaMs = (elapsedMs && syncProgressPercent >= 5 && syncProgressPercent < 100)
                ? Math.round((elapsedMs / syncProgressPercent) * (100 - syncProgressPercent))
                : null;
              return (
                <Grid item xs={12} sm={6} md={4} key={effectiveWalletId || index}>
                  <Paper
                    data-tour={index === 0 ? "wallet-card" : undefined}
                    className={classes.walletCard}
                    sx={{
                      height: "180px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between"
                    }}
                  >
                    {/* Wallet Header */}
                    <Box className={classes.walletHeader}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: "1 1 auto" }}>
                        <Box className={classes.walletIcon} style={{ backgroundColor: "transparent" }}>
                          <img
                            src={wallet.imgUrl}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              borderRadius: "50%",
                            }}
                          />
                        </Box>
                        <Box>
                          {/* Wallet Name with Alias */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: '"Manrope", Helvetica',
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              maxWidth: "200px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {wallet.name
                              ?.split(" ")
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                              .join(" ")}

                            {wallet.aliasName && (
                              <Box
                                component="span"
                                sx={{
                                  color: "#83818E",
                                  fontSize: "0.85rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "80px",
                                }}
                                title={
                                  wallet.aliasName
                                    ?.split(" ")
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                    .join(" ")
                                }
                              >
                                (
                                {wallet.aliasName.length > 6
                                  ? `${wallet.aliasName
                                    .charAt(0)
                                    .toUpperCase()}${wallet.aliasName.slice(1, 4)}...`
                                  : wallet.aliasName
                                    ?.split(" ")
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                    .join(" ")}
                                )
                              </Box>
                            )}
                          </Typography>


                          {/* Wallet Address + Copy */}
                          {wallet.walletAddress && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: '"Manrope", Helvetica',
                                  color: "#83818E",
                                  wordBreak: "break-word",
                                }}
                                title={wallet.walletAddress}
                              >
                                {`${wallet.walletAddress.slice(0, 4)}...${wallet.walletAddress.slice(-4)}`}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  const succeeded = await copyToClipboard(wallet.walletAddress);
                                  if (succeeded) {
                                    toast.success("Wallet address copied!");
                                  } else {
                                    toast.error("Couldn't copy address - please copy it manually.");
                                  }
                                }}
                              >
                                <img
                                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}/copy.svg`}
                                  alt="Copy"
                                  width={16}
                                  height={16}
                                />
                              </IconButton>
                            </Box>
                          )}

                        </Box>
                      </Box>

                      {/* Wallet Actions */}
                      <Box className={classes.walletActionsContainer}>
                        {/* Delete Icon */}
                        {!loadingStates[effectiveWalletId] && (
                          (wallet.fileUploadEnabled || userWalletStatus?.walletStatuses?.some(status =>
                            status.walletId === effectiveWalletId &&
                            (status.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_SUCCESFULLY ||
                              status.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_ERROR)
                          )) ||
                            !userWalletStatus?.walletStatuses?.some(status => status.walletId === effectiveWalletId) ? (
                            <IconButton onClick={() => openDialog(wallet, "archive")}>
                              {/* <img
                                src={`${process.env.REACT_APP_IMAGE_BASE_URL}/walletDelete.svg`}
                                alt="Delete"
                              /> */}
                              <Delete style={{ color: '#83818E' }} />
                            </IconButton>
                          ) : null
                        )}
                        {/* Sync Icon.
                            Previously this only rendered when the wallet's last known status was
                            COMPLETED_* (or had no status at all), so a wallet whose sync got stuck
                            IN_PROGRESS - e.g. its Cloud Run instance was recycled mid-sync - would
                            never show the button again, with no way for the user to retry.
                            tracking-service's CreateInitialRecordService only treats a wallet as
                            "still processing" (and skips a new sync request) for 20 minutes after
                            its last status update - past that it accepts a fresh sync regardless
                            of the old status. Mirroring that same 20-minute window here means the
                            button reappears at the same point the backend would actually honor a
                            click, instead of either hiding forever (the old bug) or showing during
                            a genuinely still-running sync (which would just be a no-op, but is
                            confusing UX). */}
                        {wallet.syncEnabled && !loadingStates[effectiveWalletId] && (() => {
                          const walletStatus = userWalletStatus?.walletStatuses?.find(
                            (status) => status.walletId === effectiveWalletId
                          );
                          if (!walletStatus) return true;
                          const isTerminal =
                            walletStatus.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_SUCCESFULLY ||
                            walletStatus.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_ERROR ||
                            walletStatus.status === process.env.REACT_APP_WALLET_SYNC_COMPLETED_TIMEOUT;
                          if (isTerminal) return true;
                          const STALE_AFTER_MINUTES = 20;
                          return moment().diff(moment(walletStatus.lastUpdatedAt), "minutes") >= STALE_AFTER_MINUTES;
                        })() && (
                          <IconButton data-tour={index === 0 ? "wallet-sync" : undefined} onClick={() => openDialog(wallet, "sync")}>
                            {/* <img
                              src={`${process.env.REACT_APP_IMAGE_BASE_URL}/refresh.svg`}
                              alt="Sync"
                            /> */}
                            <Sync style={{ color: '#83818E' }} />
                          </IconButton>
                        )}

                        {/* Manage Chains Icon - MetaMask/DEXEVM wallets only, since only they
                            can span multiple chains from a single wallet record. */}
                        {wallet.name?.toUpperCase() === 'METAMASK' && !loadingStates[effectiveWalletId] && (
                          <IconButton onClick={() => setManageChainsWallet(wallet)} title="Manage chains">
                            <TuneIcon style={{ color: '#83818E' }} />
                          </IconButton>
                        )}

                        {/* File Upload Icon */}
                        {wallet.fileUploadEnabled && !loadingStates[effectiveWalletId] && (() => {
                          const uploadKey = `uploadInProgress_${wallet.name}`;
                          const isUploadInProgress = localStorage.getItem(uploadKey) === 'true';

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Tooltip
                                title={
                                  isUploadInProgress
                                    ? `A file for ${wallet.name} is being synced. Please wait until it finishes.`
                                    : 'Upload wallet file'
                                }
                                arrow
                                placement="top"
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <IconButton
                                    onClick={() => {
                                      if (!isUploadInProgress) openDialog(wallet, 'fileupload');
                                    }}
                                    disabled={isUploadInProgress}
                                    sx={{
                                      opacity: isUploadInProgress ? 0.5 : 1,
                                      cursor: isUploadInProgress ? 'not-allowed' : 'pointer',
                                      padding: '4px',
                                      margin: 0,
                                    }}
                                  >
                                    <img
                                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}/edit.svg`}
                                      alt="Upload"
                                      style={{
                                        width: 18,
                                        height: 18,
                                        display: 'block',
                                      }}
                                    />
                                  </IconButton>
                                </Box>
                              </Tooltip>
                            </Box>
                          );
                        })()}

                      </Box>
                    </Box>
                    {/* Content Section */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                      <Box>
                        <Typography
                          style={{ color: '#83818E', fontFamily: '"Manrope", Helvetica', fontWeight: 600 }}
                          className={classes.sectionTitle}
                        >
                          Total Value
                        </Typography>

                        {!metricsLoaded ? (
                          <BoxSkeleton height={24} width="100px" />
                        ) : (() => {
                          const walletSource = wallet.name?.trim().toLowerCase();

                          // Own metrics, not the parent's - see the comment on the equivalent
                          // lookup near the top card render for why parentWalletId is not used here.
                          const metric = Array.isArray(walletMetrics)
                            ? walletMetrics.find(
                              m =>
                                m.walletId === wallet.walletId &&
                                m.source?.toLowerCase() === walletSource
                            )
                            : undefined;


                          const { value, symbol: resolvedSymbol } = resolveAssetTotal(metric?.assetTotal);

                          if (value === undefined || value === null || isNaN(Number(value))) {
                            return <Typography className={classes.sectionValue}>N/A</Typography>;
                          }

                          {/* const { display, full } = formatCompactWithTooltip(value, symbol); */ }

                          return (
                            <Tooltip title={resolvedSymbol + value} arrow>
                              {/* <Typography
                                className={classes.sectionValue}
                                sx={{ fontWeight: 'bold', fontSize: '18px', cursor: "pointer", width: "100px", overflow: "hidden", textOverflow: "ellipsis" }}
                              >
                                {symbol + value}
                              </Typography> */}

                              <Typography className={classes.sectionValue}
                                 sx={{ fontWeight: 'bold', fontSize: '18px', cursor: "pointer", width: "100px", overflow: "hidden", textOverflow: "ellipsis" }}
                              >
                                {value === undefined || value === null || isNaN(value)
                                  ? "N/A"
                                  : resolvedSymbol + new Intl.NumberFormat().format(Number(value).toFixed(2))}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </Box>

                      <Box>
                        <Typography style={{ color: '#83818E', fontFamily: '"Manrope", Helvetica', fontWeight: 600 }} className={classes.sectionTitle}>
                          Number of Transactions
                        </Typography>
                        {!metricsLoaded ? (
                          <BoxSkeleton height={24} width="50px" />
                        ) : (() => {
                          const metric = Array.isArray(walletMetrics)
                            ? walletMetrics.find(
                              m =>
                                m.walletId === wallet.walletId &&
                                m.source?.toLowerCase() === wallet.name.toLowerCase()
                            )
                            : undefined;

                          const txCount = metric?.transactionCount;
                          return (
                            <Typography className={classes.sectionValue} sx={{  fontSize: '1rem', textAlign: 'center', fontWeight: isNaN(txCount) ? 400 : 700, }}>
                              {txCount === undefined || txCount === null || isNaN(txCount)
                                ? "N/A"
                                : txCount}
                            </Typography>
                          );
                        })()}
                      </Box>
                    </Box>

                    {/* Footer */}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-tour="syncing-card">
                      {/* Check if the wallet is currently syncing.
                          isLoading/hasError are already scoped to this specific wallet's
                          live status, so they're sufficient on their own - no need for
                          isPollingActive, which is a single flag shared across every
                          wallet card and can go stale (see the polling effect above). */}
                      {loadingStates[effectiveWalletId] || (isLoading && !hasError) ? (
                        <>
                          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
                            <Typography style={{ color: "#F4A261", fontFamily: 'Manrope', fontWeight: 600, flexShrink: 0 }} className={classes.footerText}>
                              {syncProgressPercent > 0 ? `Syncing ${syncProgressPercent}%` : "Syncing"}
                            </Typography>
                            {/* Real wall-clock elapsed time + a progress-based ETA, both derived
                                from createdAt/progressPercent on the live tracking record - not
                                a static label, so this actually moves as polling refreshes it. */}
                            {(elapsedMs !== null || etaMs !== null) && (
                              <Typography
                                sx={{
                                  color: "#A19FA8", fontFamily: 'Manrope', fontSize: "0.72rem",
                                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
                                }}
                              >
                                {elapsedMs !== null && formatDurationShort(elapsedMs)}
                                {etaMs !== null && ` · ~${formatDurationShort(etaMs)} left`}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ width: '100%' }}>
                            <LinearProgress
                              variant={syncProgressPercent > 0 ? "determinate" : "indeterminate"}
                              value={syncProgressPercent > 0 ? syncProgressPercent : undefined}
                              sx={{
                                flexGrow: 1,
                                height: 6,
                                borderRadius: 5,
                                backgroundColor: "#E0E0E0",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 5,
                                  backgroundImage: "linear-gradient(90deg, #F4A261 0%, #E76F51 100%)",
                                },
                              }} />
                          </Box>
                        </>
                      ) : hasError ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                          <DoneIcon sx={{ color: "#FF0000", flexShrink: 0 }} />
                          <Typography
                            style={{ color: "#FF0000", fontFamily: 'Manrope' }}
                            className={classes.footerText}
                            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            title={`${hasTimedOut ? "Timed out" : "Failed"} ${moment(currentWalletStatus?.lastUpdatedAt || new Date()).startOf("minute").fromNow()}`}
                          >
                            {hasTimedOut ? "Timed out " : "Failed "}
                            {moment(
                              currentWalletStatus?.lastUpdatedAt || new Date()
                            ).startOf("minute").fromNow()}
                          </Typography>
                        </Box>
                      ) : userWalletStatus?.walletStatuses?.some(status => status.walletId === effectiveWalletId) ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                          <DoneIcon sx={{ color: "#4CAF50", flexShrink: 0 }} />
                          <Typography
                            style={{
                              color: "#4CAF50",
                              fontFamily: 'Manrope'
                            }}
                            className={classes.footerText}
                            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            Synced{" "}
                            {moment(currentWalletStatus?.lastUpdatedAt || new Date()).startOf("minute").fromNow()}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          style={{
                            color: "#83818E",
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                          }}
                          className={classes.footerText}
                        >
                          N/A
                        </Typography>
                      )}
                    </Box>


                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box className={classes.contentContainer} >
            <DataNotFound />
          </Box>
        )}
      </Box>

      <Box className={classes.walletContainer}>
        <Box className={classes.contentContainer}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <Typography className={classes.textClr}
              variant="h5"
              sx={(theme) => ({
                //color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000", // White for dark, black for light
                //color:theme.name === "LIGHT" ? "#000 !important" : "#FFF !important",
                fontFamily: '"Manrope", "Helvetica", "Arial", sans-serif',
                fontWeight: 600,
                textTransform: "none",
              })}
            >
              Archived Wallets
            </Typography>
          </Box>

          {/* Content */}
          { !walletsLoaded ? (
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
              <CircularProgress />
            </Box>
          ) : archivedWallets.length > 0 ? (
            <Grid container spacing={3}>
              {archivedWallets.map((wallet, index) => (
                <Grid item xs={12} sm={6} md={4} key={wallet.walletId || index}>
                  <Paper className={classes.walletCard}>
                    {/* Wallet Header */}
                    <Box className={classes.walletHeader}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: "1 1 auto" }}>
                        <Box className={classes.walletIcon} style={{ backgroundColor: "transparent" }}>
                          <img
                            src={wallet.imgUrl}
                            //alt="KuCoin Icon"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              borderRadius: "50%",
                            }}
                          />
                        </Box>
                        <Box>
                          {/* Wallet Name */}
                          <Typography variant="h6" sx={{ fontFamily: '"Manrope", Helvetica', fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                            {wallet.name
                              ?.split(" ")
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                              .join(" ")}

                            {wallet.aliasName && (
                              <Box
                                component="span"
                                sx={{
                                  color: "#83818E",
                                  fontSize: "0.85rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "80px",
                                }}
                                title={wallet.aliasName
                                  ?.split(" ")
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                  .join(" ")}
                              >
                                (
                                {wallet.aliasName.length > 6
                                  ? `${wallet.aliasName.charAt(0).toUpperCase()}${wallet.aliasName.slice(1, 4)}...`
                                  : wallet.aliasName
                                    ?.split(" ")
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                    .join(" ")}
                                )
                              </Box>
                            )}
                          </Typography>


                          {/* Wallet Address + Copy Button */}
                          {wallet.walletAddress && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#83818E',
                                  fontFamily: '"Manrope", Helvetica',
                                  fontWeight: 600,
                                  wordBreak: "break-word",
                                }}
                                title={wallet.walletAddress}
                              >
                                {`${wallet.walletAddress.slice(0, 4)}...${wallet.walletAddress.slice(-4)}`}
                              </Typography>

                              <IconButton
                                size="small"
                                onClick={async () => {
                                  const succeeded = await copyToClipboard(wallet.walletAddress);
                                  if (succeeded) {
                                    toast.success("Wallet address copied!");
                                  } else {
                                    toast.error("Couldn't copy address - please copy it manually.");
                                  }
                                }}
                              >
                                <img
                                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}/copy.svg`}
                                  alt="Copy"
                                  width={16}
                                  height={16}
                                />
                              </IconButton>
                            </Box>
                          )}

                        </Box>

                      </Box>
                      <Box>
                        <IconButton style={{ color: '#83818E' }}>
                          <UndoIcon onClick={() => openDialog(wallet, "unarchive")} />
                        </IconButton>
                        <IconButton
                          style={{ color: "#83818E" }}
                          onClick={() => openDialog(wallet, "delete")}
                          disabled={wallet.isDeleting} // Disable button when deleting
                        >
                          {wallet.isDeleting ? <CircularProgress size={20} color="inherit" /> : <Delete />}
                        </IconButton>


                      </Box>

                    </Box>

                    {/* Content Section */}
                    <div style={{ position: "absolute", bottom: 0, left: 0, width: "100%", padding: "16px", boxSizing: "border-box" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box>
                          <Typography style={{ color: '#83818E', fontFamily: '"Manrope", Helvetica', fontWeight: 600 }} className={classes.sectionTitle}>
                            Total Value
                          </Typography>
                          {(() => {
                            const metric = Array.isArray(walletMetrics)
                              ? walletMetrics.find(
                                m =>
                                  m.walletId === wallet.walletId &&
                                  m.source?.toLowerCase() === wallet.name.toLowerCase()
                              )
                              : undefined;

                            const { value, symbol: resolvedSymbol } = resolveAssetTotal(metric?.assetTotal);

                            return (
                              <Typography className={classes.sectionValue}>
                                {value === undefined || value === null || isNaN(value)
                                  ? "N/A"
                                  : resolvedSymbol + new Intl.NumberFormat().format(Number(value).toFixed(2))}
                              </Typography>
                            );
                          })()}

                        </Box>
                        <Box>
                          <Typography style={{ color: '#83818E', fontFamily: '"Manrope", Helvetica', fontWeight: 600 }} className={classes.sectionTitle}>
                            Number of Transactions
                          </Typography>
                          {(() => {
                            const metric = Array.isArray(walletMetrics)
                              ? walletMetrics.find(
                                m =>
                                  m.walletId === wallet.walletId &&
                                  m.source?.toLowerCase() === wallet.name.toLowerCase()
                              )
                              : undefined;

                            const txCount = metric?.transactionCount;

                            return (
                              <Typography className={classes.sectionValue}>
                                {txCount === undefined || txCount === null || isNaN(txCount)
                                  ? "N/A"
                                  : txCount}
                              </Typography>
                            );
                          })()}

                        </Box>
                      </Box>

                      {/* Footer */}
                      {/* <Typography style={{ color: '#83818E', textAlign: 'left' }} className={classes.footerText}>
                        Updated {
                        moment(window.sessionStorage.getItem(auth?.userData?.uid+wallet.walletId + "-lastUpdated")).startOf("minute").fromNow()}
                      </Typography> */}
                      {/* {!wallet.fileUploadEnabled && ( */}
                      <Typography style={{ color: "#83818E", textAlign: 'left' }} className={classes.footerText}>
                        Synced{" "}
                        {moment(
                          userWalletStatus?.walletStatuses?.find(
                            (status) => status.walletId === wallet.walletId
                          )?.lastUpdatedAt || new Date()
                        )
                          .startOf("minute")
                          .fromNow()}
                      </Typography>
                      {/* )} */}
                    </div>
                  </Paper>
                  {/* AlertDialog Component */}
                  {/* <AlertDialog
                    open={isDialogOpen}
                    onClose={closeDialog}
                    onConfirm={() => handleDelete(wallet.walletName)}
                    title="Confirm Delete !"
                    description={"Are you sure you want to delete the wallet? This action cannot be undone."}
                  /> */}

                </Grid>
              ))}
            </Grid>
          ) : (
            <Box className={classes.contentContainer} >
              <DataNotFound />
            </Box>
          )}
        </Box>
        {/* This is Code for Archived Wallets */}

      </Box>
      {/* This renders your dialog depending on the action */}
      {dialogData.action === "fileupload" ? (
        <FileUploadDialog
          open={dialogData.isOpen && dialogData.action === "fileupload"}
          onClose={closeDialog}
          wallet={{
            ...dialogData.wallet,
            ...walletConfigCache?.find(cfg =>
              cfg.name?.trim().toLowerCase() === dialogData.wallet?.name?.trim().toLowerCase()
            ),
           // userId: userData?.uid
          }}
        />
      ) : (
        <AlertDialog
          open={dialogData.isOpen}
          onClose={closeDialog}
          onConfirm={() => handleAction(dialogData.walletName, dialogData.walletId, dialogData.action)}
          title={dialogData.title}
          description={dialogData.description}
          confirmDisabled={isDialogActionInProgress}
        />
      )}

      <ManageChainsDialog
        open={Boolean(manageChainsWallet)}
        onClose={() => setManageChainsWallet(null)}
        wallet={manageChainsWallet}
        walletMetrics={walletMetrics}
        onChanged={() => {
          fetchUserWallets();
          fetchWalletMetrics();
        }}
      />

      {showWhichWalletsModal && (
        <WhichWalletsAreYoursModal
          open={showWhichWalletsModal}
          onDone={() => setShowWhichWalletsModal(false)}
        />
      )}

    </Box>


  );
};

export default Wallets;
